import type { PlanInfo } from '@/types';
import type { CSSProperties } from 'react';

import type { ApplicationScreen } from './data';

type WelcomeDialogsProps = {
    plans: PlanInfo[];
    primaryHref: string;
    primaryLabel: string;
    accessHref: string;
    accessLabel: string;
    screen: ApplicationScreen;
    openDialog: (id: string, trigger: HTMLElement) => void;
    closeDialog: (id?: string) => void;
    seekTo: (value: number) => void;
};

const priceFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
});

export function WelcomeDialogs({
    plans,
    primaryHref,
    primaryLabel,
    accessHref,
    accessLabel,
    screen,
    openDialog,
    closeDialog,
    seekTo,
}: WelcomeDialogsProps) {
    const detailStyle = {
        '--srv-welcome-detail-width': `${screen.width}px`,
    } as CSSProperties;

    return (
        <>
            <dialog
                id="menu-dialog"
                className="information-dialog menu-dialog"
                aria-labelledby="menu-title"
            >
                <button
                    className="dialog-close"
                    type="button"
                    onClick={() => closeDialog('menu-dialog')}
                    aria-label="Fechar menu"
                >
                    ×
                </button>
                <h2 id="menu-title">Explore a Servion.</h2>
                <nav
                    className="mobile-dialog-nav"
                    aria-label="Principal no celular"
                >
                    <button
                        type="button"
                        onClick={() => {
                            closeDialog('menu-dialog');
                            seekTo(1);
                        }}
                    >
                        Produto <span aria-hidden="true">↘</span>
                    </button>
                    <button
                        type="button"
                        onClick={(event) =>
                            openDialog('plans-dialog', event.currentTarget)
                        }
                    >
                        Planos <span aria-hidden="true">↗</span>
                    </button>
                    <button
                        type="button"
                        onClick={(event) =>
                            openDialog('faq-dialog', event.currentTarget)
                        }
                    >
                        Perguntas frequentes <span aria-hidden="true">↗</span>
                    </button>
                    <a href={accessHref}>
                        {accessLabel} <span aria-hidden="true">↗</span>
                    </a>
                </nav>
            </dialog>

            <dialog
                id="plans-dialog"
                className="information-dialog"
                aria-labelledby="plans-title"
            >
                <button
                    className="dialog-close"
                    type="button"
                    onClick={() => closeDialog('plans-dialog')}
                    aria-label="Fechar planos"
                >
                    ×
                </button>
                <h2 id="plans-title">
                    Um plano para
                    <br />a sua rotina.
                </h2>
                <p className="dialog-intro">
                    Cada empresa tem sua própria assinatura. Você gerencia todas
                    em uma só conta.
                </p>
                <div className="plan-list">
                    {plans.map((plan) => (
                        <article key={plan.slug}>
                            <div>
                                <h3>{plan.name}</h3>
                                <p>
                                    {plan.quota === null
                                        ? 'NFS-e ilimitadas'
                                        : `${plan.quota} NFS-e por mês`}
                                </p>
                            </div>
                            <p className="plan-price">
                                {priceFormatter.format(plan.amount / 100)}
                                <span>/ mês</span>
                            </p>
                        </article>
                    ))}
                </div>
                <p className="dialog-note">
                    Valores por empresa. Assinatura mensal no cartão ou compra
                    avulsa de 30 dias por boleto ou cartão.
                </p>
                <p className="dialog-features">
                    Emissão de NFS-e · Rascunhos · PDF e XML
                </p>
                <a href={primaryHref} className="button">
                    {primaryLabel} <span aria-hidden="true">↗</span>
                </a>
            </dialog>

            <dialog
                id="faq-dialog"
                className="information-dialog faq-dialog"
                aria-labelledby="faq-title"
            >
                <button
                    className="dialog-close"
                    type="button"
                    onClick={() => closeDialog('faq-dialog')}
                    aria-label="Fechar perguntas frequentes"
                >
                    ×
                </button>
                <div className="faq-heading">
                    <h2 id="faq-title">Perguntas frequentes.</h2>
                    <p className="dialog-intro">
                        Respostas às dúvidas mais comuns.
                    </p>
                </div>
                <div className="faq-list">
                    <details name="servion-faq" open>
                        <summary>Como funcionam as notas automáticas?</summary>
                        <p>
                            Configure a frequência e ative a emissão automática.
                            A Servion emite as notas conforme a regra; você
                            acompanha as próximas datas e pode pausar a
                            recorrência ou escolher revisar antes de emitir.
                        </p>
                    </details>
                    <details name="servion-faq">
                        <summary>
                            Posso salvar rascunhos e baixar os documentos?
                        </summary>
                        <p>
                            Sim. Você pode preencher sua NFS-e em etapas e
                            salvar um rascunho para continuar depois. Para notas
                            emitidas, ficam disponíveis os documentos em PDF e
                            XML e o envio por e-mail.
                        </p>
                    </details>
                    <details name="servion-faq">
                        <summary>Posso gerenciar mais de uma empresa?</summary>
                        <p>
                            Sim. Você reúne suas empresas na mesma conta e
                            alterna entre elas. Cada empresa possui seu próprio
                            plano e limite de emissão.
                        </p>
                    </details>
                    <details name="servion-faq">
                        <summary>
                            Posso trocar de plano ou cancelar a assinatura?
                        </summary>
                        <p>
                            Sim. Você pode trocar o plano da empresa ou cancelar
                            sua assinatura na área de faturamento. Ao cancelar,
                            o acesso permanece até o fim do período já pago.
                        </p>
                    </details>
                    <details name="servion-faq">
                        <summary>
                            Como minha conta e meu certificado são protegidos?
                        </summary>
                        <p>
                            A conta usa verificação de e-mail e permite ativar a
                            autenticação em duas etapas. O arquivo do
                            certificado digital e a senha usada para acessá-lo
                            são armazenados com criptografia.
                        </p>
                    </details>
                    <details name="servion-faq">
                        <summary>Como consigo suporte?</summary>
                        <p>
                            Entre na Servion e abra Suporte na barra lateral.
                            Preencha os dados do atendimento para continuar pelo
                            WhatsApp.
                        </p>
                    </details>
                </div>
                <div className="faq-contact">
                    <p>Não encontrou sua resposta?</p>
                    <button
                        className="text-link"
                        type="button"
                        onClick={(event) =>
                            openDialog('support-dialog', event.currentTarget)
                        }
                    >
                        Acessar suporte <span aria-hidden="true">↗</span>
                    </button>
                </div>
            </dialog>

            <dialog
                id="support-dialog"
                className="information-dialog support-dialog"
                aria-labelledby="support-title"
            >
                <button
                    className="dialog-close"
                    type="button"
                    onClick={() => closeDialog('support-dialog')}
                    aria-label="Fechar suporte"
                >
                    ×
                </button>
                <h2 id="support-title">Precisa de ajuda?</h2>
                <p className="dialog-intro">
                    O suporte fica disponível dentro da Servion. Entre na sua
                    conta e escolha Suporte na barra lateral.
                </p>
                <a href={accessHref} className="button">
                    {accessLabel === 'Entrar'
                        ? 'Entrar na Servion'
                        : accessLabel}{' '}
                    <span aria-hidden="true">↗</span>
                </a>
            </dialog>

            <dialog
                id="product-image-dialog"
                className="information-dialog product-image-dialog"
                aria-labelledby="product-image-title"
                style={detailStyle}
            >
                <button
                    className="dialog-close"
                    type="button"
                    onClick={() => closeDialog('product-image-dialog')}
                    aria-label="Fechar tela ampliada"
                >
                    ×
                </button>
                <p className="eyebrow">
                    Interface da Servion · dados de demonstração
                </p>
                <h2 id="product-image-title">{screen.title}</h2>
                <div className="image-viewport">
                    <img
                        id="app-screen-large"
                        src={screen.src}
                        width={screen.width}
                        height={screen.height}
                        alt={screen.alt}
                    />
                </div>
            </dialog>
        </>
    );
}
